import { createPromiseClient, Interceptor } from "@bufbuild/connect";
import { createConnectTransport } from "@bufbuild/connect-web";
import type { ServiceType } from "@bufbuild/protobuf";
import { useMemo } from "react";
import firebase from "firebase/app";
import "firebase/auth";

const authInterceptor: Interceptor = (next) => async (req) => {
  try {
    const token = await firebase.auth().currentUser?.getIdToken();
    if (token) {
      req.header.set("Authorization", `Bearer ${token}`);
    }
  } catch (err) {
    console.error(err);
  }

  return await next(req);
};

const createTransport = (
  interceptors: Interceptor[] = [],
  credentials: RequestCredentials = "same-origin"
) => {
  return createConnectTransport({
    baseUrl: process.env.REACT_APP_BACKEND_API_URL ?? "",
    useBinaryFormat: process.env.NODE_ENV === "production",
    useHttpGet: true,
    credentials,
    interceptors,
  });
};

/**
 * redux-saga中などでhooksが使えない場合用
 */
export const createConnectClient = <T extends ServiceType>(service: T) =>
  createPromiseClient(service, createTransport([authInterceptor]));

export const useConnectClient = <T extends ServiceType>(service: T) => {
  return useMemo(() => createConnectClient(service), [service]);
};

/**
 * 認証不要であればこちらを使う
 */
export const usePublicConnectClient = <T extends ServiceType>(service: T) => {
  return useMemo(
    () => createPromiseClient(service, createTransport()),
    [service]
  );
};

/**
 * クロスオリジン対応の認証不要クライアント作成（非Hook版）
 * credentials: "include"によりクロスオリジンでもCookieを送受信
 */
export const createCrossOriginPublicConnectClient = <T extends ServiceType>(
  service: T
) => createPromiseClient(service, createTransport([], "include"));

/**
 * クロスオリジン対応の認証必要クライアント作成（非Hook版）
 * credentials: "include"によりクロスオリジンでもCookieを送受信し、認証トークンも付与
 */
export const createCrossOriginPrivateConnectClient = <T extends ServiceType>(
  service: T
) =>
  createPromiseClient(service, createTransport([authInterceptor], "include"));
